import { Alert, Button, FilterPanel } from '@gsa/afp-component-library';
import React, { memo, useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { StoreOperations, VMSSubjects } from '../../../../constants/constants';
import LeasingAgencyFilterItem from '../../../my-orders/Filters/LeasingAgencyFilterItem';
import LeasingBureauFilterItem from '../../../my-orders/Filters/LeasingBureauFilterItem';
import LeasingOfficeFilterItem from '../../../my-orders/Filters/LeasingOfficeFilterItem';
import { ADDITIONAL_VEHICLE_FILTERS } from '../../utils/additional-utils';
import { setAdditionalsFilters } from '../../../../reducers/additionals-reducer';
import ZonesFilterItem from '../../../my-orders/Filters/ZonesFilterItem';
import FMCFilterItem from '../../../my-orders/Filters/FMCFilterItem';
import CustomerAccountTable from './customer-account-table';
import { useGetCustomerFSREmails } from '../../../../requests/additionals-request';

export default function SearchCustomerAccount() {
  const [searchFilters, setSearchFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [typeaheadData, setTypeaheadData] = useState([]);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const [getCustomerFSREmailsByPartialEmail] = useGetCustomerFSREmails();

  useEffect(() => {
    if (error) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [error]);

  const AgencyFilterItem = memo(({ filter }) => {
    return (
      <>
        <LeasingAgencyFilterItem
          filter={filter}
          operation={StoreOperations.View}
          subject={VMSSubjects.VEHICLE}
          placeholder="-Select-"
          defaultValueSelected
        />
      </>
    );
  });

  const BureauFilterItem = memo(({ filter }) => {
    return (
      <div className="margin-top-neg-3">
        <LeasingBureauFilterItem
          filter={filter}
          operation={StoreOperations.View}
          subject={VMSSubjects.VEHICLE}
          agencyFilterPath={ADDITIONAL_VEHICLE_FILTERS.AGENCY}
          bureauFilterPath={ADDITIONAL_VEHICLE_FILTERS.BUREAU}
          type="select"
        />
      </div>
    );
  });

  // eslint-disable-next-line no-unused-vars
  const OfficeFilterItem = memo(({ filter }) => {
    return (
      <div className="margin-top-neg-3">
        <LeasingOfficeFilterItem
          filter={filter}
          operation={StoreOperations.View}
          subject={VMSSubjects.VEHICLE}
          agencyFilterPath={ADDITIONAL_VEHICLE_FILTERS.AGENCY}
          bureauFilterPath={ADDITIONAL_VEHICLE_FILTERS.BUREAU}
          officeFilterPath={ADDITIONAL_VEHICLE_FILTERS.OFFICE}
          type="select"
        />
      </div>
    );
  });

  const customerAccountFiltersStructure = useMemo(() => {
    return [
      {
        key: 'agency-fsr-section',
        filters: [
          {
            key: ADDITIONAL_VEHICLE_FILTERS.AGENCY,
            title: 'Leasing agency',
            component: AgencyFilterItem,
            label: (
              <div className="margin-bottom-1 text-bold"> Leasing agency</div>
            ),
            permanent: false,
            operator: '$exact',
            value: '',
            placeholder: '-Select-',
          },
          {
            key: ADDITIONAL_VEHICLE_FILTERS.BOAC,
            permanent: false,
            operator: '$exact',
            label: <div className="margin-bottom-1 text-bold">BOAC</div>,
            prefixIcon: '',
            type: 'text',
            id: 'placeholder_customer_boac',
          },
          {
            key: ADDITIONAL_VEHICLE_FILTERS.ZONE,
            label: <div className="margin-bottom-1 text-bold">Zone</div>,
            component: ZonesFilterItem,
            permanent: false,
            operator: '$exact',
            value: '',
            placeholder: '-Select-',
          },
          {
            key: ADDITIONAL_VEHICLE_FILTERS.LEGACY_CUSTOMER_NUMBER,
            permanent: false,
            operator: '$exact',
            label: (
              <div className="margin-bottom-1 text-bold">
                Legacy customer number
              </div>
            ),
            prefixIcon: '',
            type: 'text',
            id: 'placeholder_legacy_customer_number',
          },
        ],
      },
      {
        key: 'bureau-zone-vin',
        filters: [
          {
            key: ADDITIONAL_VEHICLE_FILTERS.BUREAU,
            label: (
              <div className="margin-bottom-4 text-bold">Leasing bureau</div>
            ),
            component: BureauFilterItem,
            permanent: false,
            operator: '$exact',
            value: '',
            placeholder: '-Select-',
            customFieldProps: {
              wrapperClass: 'margin-top-3',
            },
          },
          {
            key: ADDITIONAL_VEHICLE_FILTERS.POC_EMAIL,
            permanent: false,
            operator: '$exact',
            label: (
              <div className="margin-bottom-1 text-bold">
                Customer POC email
              </div>
            ),
            type: 'text',
            id: 'placeholder_agency',
            ariaLabel: 'Customer POC email',
            customFieldProps: {
              wrapperClass: 'margin-top-3',
            },
          },

          {
            key: ADDITIONAL_VEHICLE_FILTERS.FMC,
            label: (
              <div className="margin-bottom-1 text-bold">
                Fleet management center
              </div>
            ),
            component: FMCFilterItem,
            permanent: false,
            operator: '$exact',
            placeholder: '-Select-',
            customFieldProps: {
              wrapperClass: 'margin-top-3',
            },
          },
        ],
      },
      {
        key: 'filter-boac',
        filters: [
          {},
          {
            key: ADDITIONAL_VEHICLE_FILTERS.FSR_EMAIL,
            permanent: false,
            operator: '$exact',
            placeholder: 'All',
            label: 'FSR email',
            value: '',
            type: 'typeahead',
            id: 'placeholder_fsr_email',
            ariaLabel: 'FSR email',
            customFieldProps: {
              inputCharNum: 3,
              promptText: 'Search requires 3 characters',
              debounceDelay: 500,
              showNoResults: true,
              clearPanelFilterOnEmpty: true,
              wrapperClass: 'margin-top-3',
            },
          },
          {
            key: ADDITIONAL_VEHICLE_FILTERS.CUSTOMER_NUMBER,
            permanent: false,
            operator: '$exact',
            label: (
              <div className="margin-bottom-1 text-bold">
                Customer account number
              </div>
            ),
            prefixIcon: '',
            type: 'text',
            id: 'placeholder_customer_number',
            customFieldProps: {
              wrapperClass: 'margin-top-3',
            },
          },
        ],
      },
    ];
  }, []);

  const handleTypeaheadSearch = ({ variables }) => {
    const { conditions } = variables.filters[0];
    const query = conditions.filter((c) => c.key === variables.field)[0]?.value;
    const fieldName = variables.field;

    if (fieldName === ADDITIONAL_VEHICLE_FILTERS.FSR_EMAIL) {
      getCustomerFSREmailsByPartialEmail({
        variables: {
          limit: 25,
          fsrUserEmail: query,
        },
      }).then(({ data }) => {
        const fsrEmails = data?.map((a) => a.fsrUserEmail);
        setTypeaheadData({
          field: fieldName,
          values: fsrEmails,
        });
      });
    }
  };

  const clearTypeaheadData = () => {
    setTypeaheadData({
      field: '',
      values: [],
    });
  };

  return (
    <>
      {error && <Alert type="error">{error}</Alert>}
      <div className="margin-top-5">
        <div className="bg-primary-lightest padding-4">
          <div className="display-flex flex-justify margin-top-1">
            <div className="margin-top-neg-05">
              <div className="font-family-sans font-heading-xl text-normal text-primary">
                Find and select customer account
              </div>
              <div className="margin-top-1 text-ink text-normal">
                Use the filters below to find your customer account. Add or
                remove filters as needed to update viewing results.
              </div>
            </div>
            <div className="margin-left-auto margin-top-1 margin-right-neg-1">
              <Button
                label={showFilters ? 'Hide filters' : 'Show filters'}
                variant="outline"
                size="small"
                leftIcon={{ name: 'filter_alt' }}
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-button"
              />
            </div>
          </div>
          <div className={!showFilters && 'display-none'}>
            <FilterPanel.FilterPanel
              filterStructure={customerAccountFiltersStructure}
              setQueryFiltersState={(filters) => {
                setSearchFilters(filters);
              }}
              clearButtonLabel="Reset all"
              mode="horizontal"
              sectionTitle={<></>}
              fetchTypeaheads={handleTypeaheadSearch}
              typeaheadData={typeaheadData}
              showSearchIcon
              showClearIcon
              hideAppliedFilters
              handleClearAll={clearTypeaheadData}
            />
            <div className="grid-row margin-top-8 margin-bottom-3 justify-space-between">
              <div>
                <Button
                  type="button"
                  variant="primary"
                  label="Search"
                  onClick={() => {
                    if (searchFilters.length === 0) {
                      setError('Apply at least one filter to see results');
                    } else {
                      setError(null);
                      dispatch(setAdditionalsFilters(searchFilters));
                    }
                  }}
                  data-testid="search"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomerAccountTable />
    </>
  );
}
